// @ts-nocheck
// Per-player remove action for the Team Detail page (/admin/team/:id).
//
// Removal SOFT-deletes the Player doc and strips the denormalized
// `registrationTeam.players` mirror. The registrationTeam id is therefore not
// optional here — the mirror is not soft-delete-aware, and an entry left behind
// keeps being read afterwards (the Stripe webhook matches players[i].email, and
// priorTeamCount's teamPlayerEmails feeds multi-team discount tiers off it).
// `/admin/players` makes that id conditional on a checkbox; that is a bug there,
// not a pattern to copy.
//
// Money: pricing is per-player, so removing a player LOWERS the team's amount
// due. If they already paid, their payment stays counted while their cost
// leaves, and the team reads overpaid until someone refunds it. We warn with the
// exact amount and let the admin proceed (deliberate — an admin cleaning up a
// duplicate paid row shouldn't be forced to refund first). Nothing here issues
// a refund.
//
// No Undo: there is no player restore endpoint. The soft delete means the row is
// recoverable in the DB, just not self-service.
import React, { useState } from 'react';
import _ from 'lodash';
import { useAuth } from '@/auth/clerkShim';
import usePlayerStore from '@/store/playerStore';
import helpers from '@/utils/helpers';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, Loader2 } from 'lucide-react';

export default function PlayerRemoveDialog({ player, registrationTeamId, amountPaid = 0, onRemoved }) {
  const { getToken } = useAuth();
  const deletePlayer = usePlayerStore((s) => s.deletePlayer(getToken));

  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const name = _.get(player, 'info.first_name')
    ? `${_.get(player, 'info.first_name', '')} ${_.get(player, 'info.last_name', '')}`.trim()
    : (player?.name || 'this player');
  const email = _.get(player, 'info.email') || player?.email || '';
  const paidCents = Number(amountPaid) || 0;

  const onConfirm = async () => {
    setRemoving(true);
    try {
      const data = await deletePlayer(player._id, registrationTeamId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setOpen(false);
      toast.success(`${name} removed from this team.`);
      onRemoved?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove player. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        title="Remove player"
        aria-label={`Remove ${name}`}
        onClick={() => setOpen(true)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <AlertDialog open={open} onOpenChange={(o) => { if (!removing) setOpen(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {email ? <>They&apos;ll be removed from this team&apos;s roster ({email}). </> : <>They&apos;ll be removed from this team&apos;s roster. </>}
                  This can&apos;t be undone from here.
                </p>
                {paidCents > 0 && (
                  <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-warning">
                    This player has paid <strong>{helpers.convertToUSD(paidCents)}</strong>. Removing them
                    lowers the team&apos;s amount due, so the team will read <strong>overpaid</strong> until
                    you refund that payment.
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirm(); }}
              disabled={removing}
              className="bg-destructive hover:bg-destructive/90"
            >
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove player'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
